import MultiStepForm from "@/components/MultiStepForm";
import { DetailsStep } from "../videoDetails";
import { useForm } from "react-hook-form";
import { CREATE_MEMBERSHIP_VIDEO_MUTATION } from "./queries";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import { Params, Path } from "@/router";
import AssessmentDetails from "../assessmentDetails";
import usePageTitle from "@/hooks/usePageTitle";
import { prepareAssessmentForSubmit } from "../_utils";
import { useEffect } from "react";
import { useFlags } from "@/hooks/useFeatureFlags";

export default function CreateVideoPage() {
  usePageTitle("/education/videos/publish-video", "Education | Videos | Publish Video");
  const form = useForm({
    defaultValues: {
      publishedAt: {
        startDate: new Date(),
        endDate: new Date()
      },
      expiredAt: null,
      title: "",
      description: "",
      url: "",
      series: "",
      creditCategory: "",
      durationInSeconds: 0,
      contentType: "vimeo",
      creditAmount: 1.0,
      scormPackage: null as File | null
    }
  });

  const [createMembershipVideo] = useMutation(CREATE_MEMBERSHIP_VIDEO_MUTATION);
  const navigate = useNavigate();
  const homePath: LinkProps<Path, Params> = { to: "/education/videos" };
  const backLink = {
    to: homePath.to,
    title: "Cancel"
  };

  const flags = useFlags();
  const scormFlagEnabled = !!flags["backoffice-scorm-courses"];
  const contentType = form.watch("contentType");
  const isScorm = scormFlagEnabled && contentType === "scorm";

  useEffect(() => {
    if (!scormFlagEnabled && contentType === "scorm") {
      form.setValue("contentType", "vimeo");
      form.setValue("scormPackage", null);
    }
  }, [scormFlagEnabled, contentType, form]);

  async function handleSubmit(data: any) {
    const isValid = await form.trigger();
    if (isValid) {
      try {
        // Omit SCORM-only fields from the legacy Vimeo payload when the flag is off.
        const { scormPackage, assessment, url, contentType, creditAmount, ...rest } = data;
        const input: Record<string, unknown> = {
          ...rest,
          publishedAt: data.publishedAt.startDate,
          expiredAt: data.expiredAt?.startDate
        };

        if (scormFlagEnabled) {
          input.contentType = contentType;
          input.creditAmount = Number(creditAmount);

          if (contentType === "scorm") {
            input.scormPackage = scormPackage;
          } else {
            input.url = url;
            input.assessment = prepareAssessmentForSubmit(assessment);
          }
        } else {
          input.url = url;
          input.assessment = prepareAssessmentForSubmit(assessment);
        }

        const response = await createMembershipVideo({ variables: { input } });

        if (response.data.createMembershipVideo.success) {
          navigate(homePath.to);
          showSnackbar("Video Published");
        } else {
          showSnackbar(response.data.createMembershipVideo.message);
        }
      } catch (e) {
        showSnackbar("Error publishing video");
      }
    }
  }

  const steps = isScorm
    ? [
        {
          headline: "Details",
          supportingText: "Step 1",
          content: <DetailsStep form={form} />
        }
      ]
    : [
        {
          headline: "Details",
          supportingText: "Step 1",
          content: <DetailsStep form={form} />
        },
        {
          headline: "Assessment",
          supportingText: "Step 2",
          content: <AssessmentDetails form={form} />
        }
      ];

  return (
    <MultiStepForm
      form={form}
      steps={steps}
      renderStepper={true}
      onSubmit={handleSubmit}
      backLink={backLink}
      modelName="Video"
      enableSubmitOnLastStep={true}
      fullWidth={true}
    />
  );
}
